import React, { useCallback, useId } from 'react'
import { formToObject } from 'lib/forms/formToObject'
import useQueryParams from 'hooks/useQueryParams'
import useModalElementContext from 'hooks/Modal/useModalElementContext'

import Heading from 'LuxKit/Typography/Heading'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import RadioInput from 'LuxKit/Radio/RadioInput'
import RadioGroup from 'LuxKit/Radio/RadioGroup'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import Group from 'components/utils/Group'
import TextButton from 'LuxKit/Button/TextButton'
import { CruiseSearchSortOptions } from './Inputs/CruiseSortDropdown'
import CruiseSearchFiltersInputs, { getCruiseSearchFormValues } from './Inputs/CruiseExtraFiltersInputs'
import useCruisePriceByNight from 'hooks/useCruisePriceByNight'

interface Props {
  listFilters: App.OfferListFilters
  countSelected?: number
}

function CruiseSearchFiltersDrawer(props: Props) {
  const { listFilters, countSelected } = props
  const formId = useId()
  const modalElement = useModalElementContext<unknown>()
  const queryParams = useQueryParams()
  const sortValue = queryParams.get('sortBy') ?? CruiseSearchSortOptions[0].value
  const cruisePriceByNight = useCruisePriceByNight()

  const onSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const values = formToObject(e.currentTarget) as unknown as Record<string, any>
    const formValues = getCruiseSearchFormValues(values)
    formValues.priceBy = cruisePriceByNight ? 'night' : 'person'
    modalElement.resolve(formValues)
  }, [cruisePriceByNight, modalElement])

  const onResetAll = useCallback(() => {
    modalElement.resolve({})
  }, [modalElement])

  return <ModalBase height="max">
    <ModalHeader title="Sort & filter" />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={20} as="form" id={formId} onSubmit={onSubmit}>
          <VerticalSpacer gap={12}>
            <Heading variant="heading4">Sort by</Heading>
            <RadioGroup>
              {CruiseSearchSortOptions.map(option => <RadioInput
                key={option.value}
                defaultChecked={sortValue === option.value}
                name="sortBy"
                value={option.value}
              >
                {option.label}
              </RadioInput>)}
            </RadioGroup>
          </VerticalSpacer>
          <div>
            <Heading variant="heading4">Filter by</Heading>
            <CruiseSearchFiltersInputs
              listFilters={listFilters}
            />
          </div>
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
    <ModalFooter>
      <Group direction="horizontal" gap={12} horizontalAlign="stretch">
        <TextButton
          kind="tertiary"
          size="large"
          onClick={onResetAll}
        >
          Reset all {!!countSelected && `(${countSelected})`}
        </TextButton>
        <TextButton
          kind="primary"
          type="submit"
          size="large"
          form={formId}
        >
          Apply
        </TextButton>
      </Group>
    </ModalFooter>
  </ModalBase>
}

export default CruiseSearchFiltersDrawer
